import { useRef, useState } from "react";
import { FileUp, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Section } from "@/components/kit";
import { FormDialog } from "@/components/ResourceTable";
import { api, brl, errMsg } from "@/lib/api";
import { AlertList, can, useBiz, useBizGet } from "./shared";

const FIELDS = [
  { name: "month", label: "Competência (AAAA-MM)" },
  { name: "folha", label: "Folha + INSS/CPP do mês (R$)", type: "number" },
  { name: "aliquota_pct", label: "Alíquota efetiva do Simples (%)", type: "number" },
  { name: "fator_r_12m", label: "Fator R · últimos 12 meses (%)", type: "number", nullable: true, required: false },
  { name: "fator_r_mes", label: "Fator R · mês atual (%)", type: "number", nullable: true, required: false },
  { name: "limite_anexo3", label: "Faturamento máximo no mês p/ Anexo III (R$)", type: "number", nullable: true, required: false },
  { name: "rbt12", label: "RBT12 projetada (R$)", type: "number", nullable: true, required: false },
];
const br = (v) => (v == null ? "" : Number(v).toLocaleString("pt-BR", { maximumFractionDigits: 2 }));
const pct = (v) => (v == null ? "—" : `${br(v)}%`);

const Stat = ({ label, value, testid }) => (
  <div className="rounded-lg border border-[#1E3A6E] bg-[#0B1A33] p-3"><div className="text-[11px] uppercase tracking-wide text-slate-400">{label}</div>
    <div className="mt-1 font-mono text-base text-white" data-testid={testid}>{value}</div></div>
);

export function TaxReport({ onSaved }) {
  const { cid, month, role } = useBiz();
  const { data, reload } = useBizGet(`/tax-report?month=${month}`);
  const [review, setReview] = useState(null);
  const [busy, setBusy] = useState(false);
  const ref = useRef();
  const pick = async (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) { return; }
    setBusy(true);
    const fd = new FormData(); fd.append("file", f);
    try {
      const { data: p } = await api.post(`/biz/companies/${cid}/tax-report/preview`, fd);
      if (p.cnpj_warning) { toast.warning(`O CNPJ do PDF (${p.cnpj}) é diferente do cadastrado nesta empresa. Confira antes de salvar.`); }
      setReview({ ...p, month: p.month || month, ...Object.fromEntries(FIELDS.slice(1).map((x) => [x.name, br(p[x.name])])) });
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const save = async (v) => {
    await api.post(`/biz/companies/${cid}/tax-report/confirm`, { ...v, file_id: review.file_id, file_name: review.file_name });
    toast.success(`Relatório de ${v.month} aplicado aos cálculos`); reload(); onSaved();
  };
  const remove = async () => {
    if (!window.confirm("Remover o relatório deste mês? A folha e o imposto calculados por ele deixam de contar.")) { return; }
    try { await api.delete(`/biz/companies/${cid}/tax-report/${r.month}`); toast.success("Relatório removido"); reload(); onSaved(); } catch (err) { toast.error(errMsg(err)); }
  };
  const r = data?.report;
  const used = r?.limite_anexo3 && data.same_month ? Math.min(100, (data.revenue_month / r.limite_anexo3) * 100) : null;
  return (
    <Section title="Relatório contábil do mês (Fator R / Simples Nacional)" testid="biz-tax-report"
      action={can(role, "gestor") && <>
        <input ref={ref} type="file" accept=".pdf" className="hidden" onChange={pick} data-testid="biz-tax-input" />
        <Button size="sm" disabled={busy} onClick={() => ref.current.click()} data-testid="biz-tax-upload-btn"><FileUp className="mr-1 h-4 w-4" />{busy ? "Lendo PDF..." : "Anexar relatório do mês"}</Button>
      </>}>
      {!r ? <p className="text-sm text-slate-400" data-testid="biz-tax-empty">Anexe o PDF do Relatório Fator R enviado pelo escritório. A folha + INSS vira despesa fixa do mês, a alíquota efetiva calcula o imposto sobre o faturamento lançado e o limite do Anexo III passa a ser monitorado.</p> : (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2 text-xs text-slate-400">
            <span data-testid="biz-tax-month">Competência {r.month.slice(5)}/{r.month.slice(0, 4)}{!data.same_month && " (último relatório anexado; o mês selecionado ainda não tem relatório)"}</span>
            {r.file_id && <a className="text-[#F5D77F] underline" href={`${process.env.REACT_APP_BACKEND_URL}/api/biz/companies/${cid}/attachments/${r.file_id}`} target="_blank" rel="noreferrer" data-testid="biz-tax-file">ver PDF</a>}
            {can(role, "gestor") && <button onClick={remove} className="inline-flex items-center gap-1 text-red-300 hover:text-red-200" data-testid="biz-tax-remove"><Trash2 className="h-3.5 w-3.5" /> remover</button>}
          </div>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
            <Stat label="Folha + INSS" value={brl(r.folha)} testid="biz-tax-folha" />
            <Stat label="Alíquota efetiva" value={pct(r.aliquota_pct)} testid="biz-tax-aliquota" />
            <Stat label="Fator R 12m" value={pct(r.fator_r_12m)} testid="biz-tax-fr12" />
            <Stat label="Fator R mês" value={pct(r.fator_r_mes)} testid="biz-tax-frmes" />
            <Stat label="Limite Anexo III" value={r.limite_anexo3 == null ? "—" : brl(r.limite_anexo3)} testid="biz-tax-limite" />
            <Stat label="RBT12 projetada" value={r.rbt12 == null ? "—" : brl(r.rbt12)} testid="biz-tax-rbt12" />
          </div>
          {used != null && (
            <div data-testid="biz-tax-usage">
              <div className="mb-1 flex justify-between text-xs text-slate-400"><span>Faturamento lançado {brl(data.revenue_month)} de {brl(r.limite_anexo3)}</span><span>{br(used)}%</span></div>
              <div className="h-2 rounded-full bg-[#1E3A6E]"><div className={`h-2 rounded-full ${used >= 100 ? "bg-red-500" : used >= 85 ? "bg-amber-400" : "bg-emerald-500"}`} style={{ width: `${used}%` }} /></div>
            </div>
          )}
          {data.alerts.length > 0 && <AlertList alerts={data.alerts} />}
        </div>
      )}
      <FormDialog open={!!review} onOpenChange={(o) => { if (!o) { setReview(null); } }} title="Confira os valores lidos do PDF" fields={FIELDS} initial={review} onSubmit={save} testid="biz-tax-dialog" />
    </Section>
  );
}
